import React, { useState, useRef, useEffect } from 'react';
import {
  Download,
  RefreshCw,
  UploadCloud,
  AlertTriangle,
  X,
  Copy,
} from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { shipmentChargesService } from '../../../services/shipmentCharges';
import { useModal } from '../../../hooks/useModal';

interface UploadHistoryItem {
  no: number;
  date: string;
  fileName: string;
  totalRows: number;
  validShipments: number;
  invalidCount: number;
  invalidTrackingNumbers?: string[];
  status: 'SUCCESS' | 'PARTIAL' | 'FAILED';
}

const STORAGE_KEY = 'shipment_charges_upload_history';

// Modal content for displaying invalid / unmatched tracking numbers
const InvalidAWBsModal: React.FC<{
  fileName: string;
  awbs: string[];
  onClose: () => void;
}> = ({ fileName, awbs, onClose }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(awbs.join('\n'));
    toast.success(`${awbs.length} tracking numbers copied`);
  };

  return (
    <div className="space-y-4 w-full">
      <div className="flex items-center gap-3 pb-3 border-b pr-8">
        <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 shrink-0">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">
            Unmatched Tracking Numbers
          </h3>
          <p className="text-xs text-gray-500">
            File: <span className="font-semibold text-gray-700">{fileName}</span> ({awbs.length} failed)
          </p>
        </div>
      </div>

      <div className="rounded-xl bg-amber-50/80 border border-amber-200/60 p-3 text-xs text-amber-900 leading-relaxed">
        The tracking numbers below were not found in the database. Please verify these AWBs with the courier billing report.
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-gray-500 font-medium">
          <span>Failed AWBs ({awbs.length})</span>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 text-teal hover:text-tealDark font-semibold hover:underline cursor-pointer"
          >
            <Copy className="h-3.5 w-3.5" />
            Copy All
          </button>
        </div>

        <div className="max-h-60 overflow-y-auto space-y-1.5 pr-1 divide-y divide-gray-100 rounded-xl border border-gray-100 bg-gray-50/60 p-2">
          {awbs.map((awb, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between py-1.5 px-2 bg-white rounded-lg border border-gray-100 text-xs font-mono text-gray-800"
            >
              <span>{awb}</span>
              <span className="text-[10px] uppercase font-sans font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded">
                Not Found
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={onClose} variant="secondary" className="px-6">
          Close
        </Button>
      </div>
    </div>
  );
};

export default function ShipmentChargesAdminPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [history, setHistory] = useState<UploadHistoryItem[]>([]);
  const { open: openModal, close: closeModal, Modal } = useModal();

  // Load history from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setHistory(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Failed to load upload history', e);
    }
  }, []);

  const saveHistory = (items: UploadHistoryItem[]) => {
    setHistory(items);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error('Failed to save upload history', e);
    }
  };

  const openErrorModal = (fileName: string, awbs: string[]) => {
    openModal({
      element: (
        <InvalidAWBsModal
          fileName={fileName}
          awbs={awbs}
          onClose={closeModal}
        />
      ),
      closable: true,
      className: 'w-full max-w-lg rounded-2xl',
    });
  };

  // Sample Excel generator
  const downloadSampleTemplate = () => {
    try {
      const sampleData = [
        ['tracking', 'weight'],
        ['153701850366450', 2.0],
        ['153886460100010', 1.0],
        ['OVER0000000726', 2.0],
      ];
      const worksheet = XLSX.utils.aoa_to_sheet(sampleData);
      worksheet['!cols'] = [{ wch: 24 }, { wch: 12 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'CourierCharges');
      XLSX.writeFile(workbook, 'shipment_charges_sample.xlsx');
      toast.success('Sample template downloaded');
    } catch (err) {
      toast.error('Failed to generate sample template');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validExtensions = ['.xlsx', '.xls'];
    const hasValidExt = validExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

    if (!hasValidExt) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setIsUploading(true);
    const toastId = toast.loading('Processing shipment charges Excel...');

    try {
      const response = await shipmentChargesService.uploadShipmentCharges(formData);
      toast.success(response.message || 'File uploaded & processed successfully', {
        id: toastId,
      });

      const invalidList = response.invalidTrackingNumbers || [];
      const isFullSuccess = invalidList.length === 0;

      const newHistoryItem: UploadHistoryItem = {
        no: 1,
        date: format(new Date(), 'dd-MM-yyyy HH:mm'),
        fileName: file.name,
        totalRows: response.totalRows || 0,
        validShipments: response.validShipments || 0,
        invalidCount: invalidList.length,
        invalidTrackingNumbers: invalidList,
        status: isFullSuccess ? 'SUCCESS' : response.validShipments > 0 ? 'PARTIAL' : 'FAILED',
      };

      const updatedHistory = [
        newHistoryItem,
        ...history.map((item, idx) => ({ ...item, no: idx + 2 })),
      ].slice(0, 50);

      saveHistory(updatedHistory);

      // Open error modal popup if there are any unmatched AWBs
      if (invalidList.length > 0) {
        openErrorModal(file.name, invalidList);
      }
    } catch (error: any) {
      const errMsg =
        error.response?.data?.message ||
        (typeof error.response?.data === 'string'
          ? error.response.data
          : null) ||
        error.message ||
        'Failed to process file';

      toast.error(errMsg, { id: toastId });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRefresh = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setHistory(JSON.parse(saved));
      }
    } catch (e) {
      // ignore
    }
    toast.success('History refreshed');
  };

  return (
    <>
      <Modal />
      <div className="min-h-screen py-6">
        <div className="mx-auto space-y-6 sm:px-4 md:px-4 lg:px-0">
          {/* Header */}
          <div className="flex flex-col md:pl-0 pl-4">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              Shipment Charges
            </h1>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Manage courier weight reconciliation and wallet deductions</span>
            </div>
          </div>

          {/* Upload Action Box - Exact Outward Remittance Design */}
          <div className="relative mx-4 overflow-hidden rounded-3xl border border-gray-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
            {/* Background Decorative Element */}
            <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-teal/5 blur-3xl transition-all duration-700" />

            <div className="relative flex flex-col items-center justify-between gap-8 lg:flex-row">
              <div className="flex-1 space-y-3 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 rounded-full bg-teal/10 px-3 py-1 text-xs font-bold text-teal">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal opacity-75"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-teal"></span>
                  </span>
                  QUICK UPLOAD
                </div>
                <h3 className="text-xl font-extrabold text-gray-900 tracking-tight">
                  Import Courier Weight Charges
                </h3>
                <p className="max-w-xl text-sm leading-relaxed text-gray-500">
                  Download our standardized sample file, populate it with your courier weight data, and upload it back for processing.
                  <span className="mt-2 block font-medium text-teal/70">
                    Ensure tracking number and courier weight columns are accurately filled.
                  </span>
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-5 shrink-0">
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".xlsx, .xls"
                  onChange={handleFileUpload}
                />

                {/* Sample Template Download Card */}
                <div
                  onClick={downloadSampleTemplate}
                  className="group relative flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 pr-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-teal/20 hover:shadow-md cursor-pointer active:scale-95"
                >
                  <div className="absolute right-3 top-3 rounded-md bg-green-500 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-tighter text-white">
                    XLSX
                  </div>
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-green-50 transition-colors group-hover:bg-green-100">
                    <img
                      src="/excel.png"
                      alt="Excel"
                      className="h-10 w-10 object-contain drop-shadow-sm transition-transform duration-300 group-hover:scale-110"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-gray-900 group-hover:text-teal transition-colors">
                      Sample Template
                    </span>
                    <span className="text-[11px] font-medium text-gray-400">
                      Download Format
                    </span>
                  </div>
                  <div className="ml-2 flex h-8 w-8 items-center justify-center rounded-full bg-gray-50 text-gray-400 transition-all group-hover:bg-teal group-hover:text-white">
                    <Download className="h-4 w-4" />
                  </div>
                </div>

                {/* Upload Button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="group flex items-center gap-3 rounded-lg bg-gradient-to-br from-teal to-tealDark px-8 py-4 font-bold text-white shadow-lg shadow-teal/20 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-teal/30 active:scale-95 disabled:opacity-50"
                >
                  <div className="rounded-lg bg-white/20 p-1 transition-transform duration-500 group-hover:rotate-12">
                    {isUploading ? (
                      <RefreshCw className="h-5 w-5 text-white animate-spin" />
                    ) : (
                      <UploadCloud className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <span>{isUploading ? 'Uploading...' : 'Upload File'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* History Section */}
          {history.length > 0 && (
            <div className="space-y-4 mx-4">
              <div className="flex justify-end pr-2 gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefresh}
                  className="text-[#006060] font-medium"
                >
                  <RefreshCw className="h-4 w-4 mr-1.5" />
                  Refresh
                </Button>
              </div>

              {/* History Table Header (Desktop) */}
              <div className="hidden lg:block bg-teal text-white rounded-xl px-4 py-3 shadow-md text-xs xl:text-sm font-bold">
                <div className="grid grid-cols-[50px_160px_1fr_100px_130px_130px_120px] gap-4 items-center text-center">
                  <div className="text-left pl-2">No</div>
                  <div>Date and Time</div>
                  <div className="text-left">File Name</div>
                  <div>Orders</div>
                  <div>Successful</div>
                  <div>Error</div>
                  <div>Action</div>
                </div>
              </div>

              {/* History List (Desktop) */}
              <div className="hidden lg:block space-y-3">
                {history.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white border rounded-xl px-4 py-3 shadow-sm hover:shadow-md transition-all"
                  >
                    <div className="grid grid-cols-[50px_160px_1fr_100px_130px_130px_120px] gap-4 items-center text-center text-sm">
                      <div className="text-left pl-2 text-gray-500 font-medium">
                        {item.no}.
                      </div>
                      <div className="text-gray-700">{item.date}</div>
                      <div className="text-left text-gray-900 font-medium truncate pr-4">
                        {item.fileName}
                      </div>
                      <div className="font-semibold text-gray-800">
                        {item.totalRows}
                      </div>
                      <div className="font-semibold text-green-600">
                        {item.validShipments}
                      </div>
                      <div className="font-semibold text-red-600">
                        {item.invalidCount}
                      </div>
                      <div>
                        {item.invalidCount > 0 ? (
                          <button
                            onClick={() =>
                              openErrorModal(
                                item.fileName,
                                item.invalidTrackingNumbers || []
                              )
                            }
                            className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 transition-colors cursor-pointer"
                          >
                            View Errors ({item.invalidCount})
                          </button>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Complete
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {history.map((item, idx) => (
                  <Card key={idx} className="p-4 space-y-2 border shadow-sm">
                    <div className="flex justify-between items-center text-xs text-gray-500">
                      <span>#{item.no}</span>
                      <span>{item.date}</span>
                    </div>
                    <div className="font-semibold text-gray-900 truncate">
                      {item.fileName}
                    </div>
                    <div className="grid grid-cols-3 gap-2 pt-2 border-t text-center text-xs">
                      <div>
                        <span className="text-gray-400 block">Orders</span>
                        <span className="font-bold text-gray-800">{item.totalRows}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block">Successful</span>
                        <span className="font-bold text-green-600">{item.validShipments}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block">Error</span>
                        <span className="font-bold text-red-600">{item.invalidCount}</span>
                      </div>
                    </div>
                    {item.invalidCount > 0 && (
                      <div className="pt-2 text-right">
                        <button
                          onClick={() =>
                            openErrorModal(
                              item.fileName,
                              item.invalidTrackingNumbers || []
                            )
                          }
                          className="text-xs font-bold text-red-600 hover:underline"
                        >
                          View Errors ({item.invalidCount})
                        </button>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
